/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiClient } from '../api';
import { Path } from '../api/backendUrl';
import { logError } from '../utils/logger';

export interface CreateBookingPaymentIntentParams {
  /**
   * Payment type recorded on the payment doc. Must be 'booking' for the
   * booking rail — the payment service routes on this value.
   */
  paymentType: string;
  /**
   * Booking identifier (BID). The payment service fetches the booking
   * server-side via this id (S2S) and derives the charge amount from the
   * booking record — never from the browser.
   */
  referenceId: string;
  description?: string;
  metadata?: Record<string, string>;
}

export interface BookingPaymentIntent {
  clientSecret: string;
  paymentIntentId?: string;
}

/**
 * Create a Stripe PaymentIntent for an online booking payment.
 *
 * POST /payment/api/v1/payments/pay (gigmosaic-payment-service). The backend
 * fetches the booking server-side via `referenceId` (the bookingId), derives
 * the charge amount from the booking record, and creates the PaymentIntent
 * with `capture_method: 'manual'`, returning its `clientSecret` so the FE can
 * confirm it with the Stripe PaymentElement. The amount is NEVER supplied by
 * the browser — it would be an auth-boundary violation on money. Capture
 * happens server-side after authorization (webhook), which flips the booking
 * to Confirmed — the FE polls booking status until that lands.
 *
 * ⚠️ The FE never sees, stores, or transmits raw card data — Stripe's
 * PaymentElement collects it directly inside Stripe's own iframe.
 */
export const createBookingPaymentIntent = async (
  params: CreateBookingPaymentIntentParams
): Promise<BookingPaymentIntent> => {
  try {
    const res = await apiClient.post(Path.paymentIntent, {
      paymentType: params.paymentType,
      referenceId: params.referenceId,
      description: params.description,
      metadata: params.metadata,
    });

    const data = res.data;
    // Tolerate the usual response envelopes (data at root / under .data / .result).
    const clientSecret =
      data?.clientSecret ||
      data?.data?.clientSecret ||
      data?.result?.clientSecret ||
      data?.paymentIntent?.clientSecret;
    const paymentIntentId =
      data?.paymentIntentId ||
      data?.data?.paymentIntentId ||
      data?.result?.paymentIntentId ||
      data?.paymentIntent?.id;

    if (!clientSecret) {
      throw new Error(
        'Payment service did not return a clientSecret. The booking payment endpoint must return the Stripe PaymentIntent clientSecret.'
      );
    }

    return { clientSecret, paymentIntentId };
  } catch (error: unknown) {
    logError('Error creating booking payment intent: ', { error });
    throw error;
  }
};
